// Every analytics read goes through these query factories, so the page and the
// sidebar's hover-prefetch build identical keys and share one cache.
//
// The page reads Postgres only — three RPCs in parallel, no edge function and
// no LLM while loading. Freshness is the server's job (the analytics cron tick);
// the page only offers a manual Refresh and polls while a refresh runs.

import { useEffect, useReducer, useRef } from 'react';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type PlaceholderDataFunction,
  type QueryClient,
} from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  AnalyticsPlatform,
  AnalyticsWindow,
  BrandAnalytics,
  DateRange,
  PostSort,
  PostsPage,
  RefreshResult,
  SocialPlatform,
  WindowAnalytics,
} from './types';
import { defaultAnalyticsWindow } from './window';
import { isLiveLease, leaseAgeMs, nextLeaseExpiry } from './freshness';

export const POSTS_PAGE_SIZE = 10;
const GC_TIME = 30 * 60 * 1000;
const POLL_FAST_MS = 5_000;
const POLL_SLOW_MS = 30_000;
/** Most refreshes end within seconds; one still running after this is a deep
 *  first listing (fetching alone may take two minutes) or a run that died. */
const FAST_FOR_MS = 2 * 60_000;

async function rpc<T>(fn: string, args: Record<string, unknown>, signal: AbortSignal): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args).abortSignal(signal);
  if (error) throw error;
  return data as T;
}

export const analyticsKeys = {
  brandRoot: (brandId: string) => ['analytics', brandId] as const,
  brand: (brandId: string) => ['analytics', brandId, 'brand'] as const,
  window: (brandId: string, w: AnalyticsWindow) =>
    ['analytics', brandId, 'window', w.current.start, w.current.end, w.previous?.start ?? null, w.previous?.end ?? null] as const,
  posts: (brandId: string, range: DateRange, platform: SocialPlatform | null, sort: PostSort, page: number) =>
    ['analytics', brandId, 'posts', range.start, range.end, platform, sort, page] as const,
};

/** Keep showing the previous data while new dates or a new page load — but
 *  only while `same` holds for the previous key. react-query's keepPreviousData
 *  would show brand A's numbers under brand B's name until B's request
 *  returns, and LinkedIn's posts under the Instagram tab. */
function placeholderWhile<T>(
  same: (previousKey: readonly unknown[]) => boolean,
): PlaceholderDataFunction<T, Error, T, readonly unknown[]> {
  return (previous, previousQuery) => (previousQuery && same(previousQuery.queryKey) ? previous : undefined);
}

export const analyticsQueries = {
  brand: (brandId: string) =>
    queryOptions({
      queryKey: analyticsKeys.brand(brandId),
      queryFn: ({ signal }) => rpc<BrandAnalytics>('get_analytics_brand', { p_brand_id: brandId }, signal),
      staleTime: 60_000,
      gcTime: GC_TIME,
    }),

  window: (brandId: string, w: AnalyticsWindow) =>
    queryOptions({
      queryKey: analyticsKeys.window(brandId, w),
      queryFn: ({ signal }) =>
        rpc<WindowAnalytics>('get_analytics_window', {
          p_brand_id: brandId,
          p_start: w.current.start,
          p_end: w.current.end,
          p_cmp_start: w.previous?.start ?? null,
          p_cmp_end: w.previous?.end ?? null,
        }, signal),
      staleTime: 5 * 60_000,
      gcTime: GC_TIME,
      placeholderData: placeholderWhile<WindowAnalytics>((key) => key[1] === brandId),
    }),

  posts: (brandId: string, range: DateRange, platform: SocialPlatform | null, sort: PostSort, page: number) =>
    queryOptions({
      queryKey: analyticsKeys.posts(brandId, range, platform, sort, page),
      queryFn: ({ signal }) =>
        rpc<PostsPage>('get_analytics_posts', {
          p_brand_id: brandId,
          p_start: range.start,
          p_end: range.end,
          p_platform: platform,
          p_sort: sort,
          p_limit: POSTS_PAGE_SIZE,
          p_offset: page * POSTS_PAGE_SIZE,
        }, signal),
      staleTime: 5 * 60_000,
      gcTime: GC_TIME,
      // analyticsKeys.posts: [1] is the brand, [5] the platform.
      placeholderData: placeholderWhile<PostsPage>((key) => key[1] === brandId && key[5] === platform),
    }),
};

/** Warm the cache before the page mounts (sidebar hover/focus/press). */
export function prefetchAnalytics(qc: QueryClient, brandId: string): void {
  if (!brandId) return;
  const w = defaultAnalyticsWindow();
  void qc.prefetchQuery(analyticsQueries.brand(brandId));
  void qc.prefetchQuery(analyticsQueries.window(brandId, w));
  void qc.prefetchQuery(analyticsQueries.posts(brandId, w.current, null, 'recent', 0));
}

/** How often to re-read the brand while any platform holds a live refresh
 *  lease (ours or the cron's): every 5 s for the first two minutes of the
 *  newest lease, every 30 s after. A lease runs out 10 minutes after it was
 *  taken (isLiveLease, aged from `readAt` — when this read arrived), so
 *  neither a run that died nor reads that keep failing can keep the page
 *  polling. */
export function pollInterval(data: BrandAnalytics | undefined, readAt: number, now: number = Date.now()): number | false {
  const ages = (data?.sync ?? [])
    .filter((s) => isLiveLease(s, readAt, now))
    .map((s) => leaseAgeMs(s, readAt, now)!);
  if (ages.length === 0) return false;
  return Math.min(...ages) < FAST_FOR_MS ? POLL_FAST_MS : POLL_SLOW_MS;
}

/** Re-read everything dated for one brand: the window and the post pages. */
function invalidateDated(qc: QueryClient, brandId: string): Promise<void> {
  return qc.invalidateQueries({
    queryKey: analyticsKeys.brandRoot(brandId),
    predicate: (q) => q.queryKey[2] !== 'brand',
  });
}

/** The brand-level read, polling while a refresh runs (pollInterval). When a
 *  refresh ends, the window and post queries are re-read once. */
export function useAnalyticsBrand(brandId: string, enabled: boolean) {
  const qc = useQueryClient();
  const query = useQuery({
    ...analyticsQueries.brand(brandId),
    enabled: enabled && !!brandId,
    refetchInterval: (q) => pollInterval(q.state.data, q.state.dataUpdatedAt),
  });

  // A lease can run out with no read saying so — reads failing, a run that died
  // between polls, or a hidden tab whose polling is paused — and nothing else
  // would re-render the page or read the brand again: it would stay on
  // "Refreshing…" with Refresh disabled. When the next lease runs out,
  // re-render and re-read; `tick` re-arms the timer for the lease after it.
  const [tick, rerender] = useReducer((n: number) => n + 1, 0);
  const readAt = query.dataUpdatedAt;
  useEffect(() => {
    if (!query.data) return;
    const at = nextLeaseExpiry(query.data.sync, readAt);
    if (at === null) return;
    const timer = setTimeout(() => {
      rerender();
      // Refetches an active query even in a hidden tab, unlike the interval.
      void qc.invalidateQueries({ queryKey: analyticsKeys.brand(brandId) });
    }, Math.max(0, at - Date.now()) + 50);
    return () => clearTimeout(timer);
  }, [query.data, readAt, tick, brandId, qc]);

  // Remembered per brand: a brand left while refreshing and opened again after
  // its refresh ended still gets its re-read, and switching brands cannot
  // clear another brand's memory.
  const refreshing = query.data?.sync.some((s) => isLiveLease(s, readAt)) ?? false;
  const wasRefreshing = useRef(new Map<string, boolean>());
  useEffect(() => {
    if (!query.data) return;
    if (wasRefreshing.current.get(brandId) && !refreshing) void invalidateDated(qc, brandId);
    wasRefreshing.current.set(brandId, refreshing);
  }, [brandId, refreshing, query.data, qc]);

  return query;
}

/** The newest successful refresh in a brand read, as epoch ms (0: none). */
function newestSuccess(brand: BrandAnalytics | undefined): number {
  return Math.max(0, ...(brand?.sync ?? []).map((s) => Date.parse(s.last_success_at ?? '') || 0));
}

export interface RefreshRequest {
  brandId: string;
  /** Only these platforms (a reconnect re-checks what it fixed); all when absent. */
  platforms?: AnalyticsPlatform[];
}

/** Ask the server to refresh a brand's connected platforms now:
 *  `refresh.mutate({ brandId })`. The brand travels with the call, not with the
 *  hook, so a brand switch while it is in flight can't redirect its follow-up
 *  reads to the brand now on screen. The server answers at once; re-reading
 *  the brand picks up the leases, which starts the polling. */
export function useRefreshAnalytics() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ brandId, platforms }: RefreshRequest) => {
      const ask = async (platform?: AnalyticsPlatform) => {
        const { data, error } = await supabase.functions.invoke('refresh-analytics-for-brand', {
          body: platform ? { brandId, platform } : { brandId },
        });
        if (error) throw error;
        return data as RefreshResult;
      };
      if (!platforms?.length) return ask();
      const results = await Promise.all(platforms.map((p) => ask(p)));
      return {
        started: results.flatMap((r) => r.started),
        skipped: results.flatMap((r) => r.skipped ?? []),
      } satisfies RefreshResult;
    },
    onMutate: ({ brandId }) => ({ before: newestSuccess(qc.getQueryData<BrandAnalytics>(analyticsKeys.brand(brandId))) }),
    onSuccess: async (result, { brandId }, context) => {
      await qc.invalidateQueries({ queryKey: analyticsKeys.brand(brandId) });
      // Most runs take about a second, so ours — or another member's or the
      // cron's, when ours was refused — can end before that re-read lands.
      // Then no lease is ever seen ending, and the dated queries must be
      // re-read here: whenever no lease is live and a run started or finished.
      const read = qc.getQueryState<BrandAnalytics>(analyticsKeys.brand(brandId));
      const brand = read?.data;
      const live = brand?.sync.some((s) => isLiveLease(s, read!.dataUpdatedAt)) ?? false;
      const moved = newestSuccess(brand) > (context?.before ?? 0);
      if (!live && (result.started.length > 0 || moved)) void invalidateDated(qc, brandId);
    },
    onError: (_error, { brandId }) => qc.invalidateQueries({ queryKey: analyticsKeys.brand(brandId) }),
  });
}
